import { ReactComponent as TrashSVG } from "@/assets/icons/ic_trash.svg";
import BaseMaterialIconButton from "@/components/reusables/BaseMaterialIconButton";
import UIButton from "@/components/reusables/UIButton";
import { toastError } from "@/helpers/utils";
import { useUpdateShopifyLocationsMappingMutation } from "@/redux/features/integrations/shopifyApi";
import BaseCollapse from "@reusables/BaseCollapse";
import { ArrowRight, ChevronDown, Plus, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ShopifyLocationDropdown from "./ShopifyLocationDropdown";
import SuppliLocationDropdown from "./SuppliLocationDropdown";
import { LocationsMapperFormTyping } from "./utils";
import { useAvailableShopifyLocations, useAvailableSuppliLocations, useHasLocationMappingChanges } from "./hooks";

export default function LocationsMapper() {
    const { t } = useTranslation("", {
        keyPrefix: "imports.modules.shopify.importModal.locationsMapper",
    });
    const [isOpen, setIsOpen] = useState(true);

    // Mutation hook for updating mappings
    const [updateMappings, { isLoading: isUpdating }] = useUpdateShopifyLocationsMappingMutation();

    // RHF hooks for form management
    const { control, handleSubmit } = useForm<LocationsMapperFormTyping>({
        defaultValues: {
            mappings: [
                {
                    shopify_location: null,
                    suppli_location: null,
                },
            ],
        },
    });

    const {
        fields: mappings,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "mappings",
    });

    // Watch current form values for change detection
    const currentMappings = useWatch({
        name: "mappings",
        control,
    });

    const hasChanges = useHasLocationMappingChanges(currentMappings);

    const { getAvailableOptions: getAvailableShopifyOptions, isLoading: isShopifyOptionsLoading } =
        useAvailableShopifyLocations(currentMappings);
    const { getAvailableOptions: getAvailableSuppliOptions, isLoading: isSuppliOptionsLoading } =
        useAvailableSuppliLocations(currentMappings);

    const onSync = handleSubmit(async (data) => {
        // Filter out completely empty mappings (both locations null/undefined)
        const filteredMappings = data.mappings
            .filter((m) => m.shopify_location && m.suppli_location)
            .map((m) => ({
                shopify_location_id: m.shopify_location!.shopify_id ?? "",
                location: {
                    store_id: m.suppli_location!.store.id,
                    section_id: m.suppli_location!.section?.id,
                },
            }));

        await updateMappings({
            mappings: filteredMappings,
        })
            .unwrap()
            .then(() => {
                toast.success(t("sync.responses.success"));
            })
            .catch(toastError);
    });

    return (
        <BaseCollapse
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            loading={isUpdating || isShopifyOptionsLoading || isSuppliOptionsLoading}
        >
            <BaseCollapse.Header
                className="flex flex-row items-center justify-between space-x-2 text-accent bg-[#ECEDFE]">
                <div>
                    <div className="text-[#5A5587] font-semibold">{t("title")}</div>
                    <div className="text-[#5A5587] text-sm font-thin">{t("description")}.</div>
                </div>
                <div className="space-x-2">
                    <UIButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            onSync();
                        }}
                        size="md"
                        disabled={!hasChanges || isUpdating}
                        variant={"outline"}
                        className="space-x-2"
                    >
                        <RefreshCcw size={14} />
                        <span>{t("sync.button")}</span>
                    </UIButton>
                    <ChevronDown
                        className="w-5 h-5 text-gray-600"
                        style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 200ms",
                        }}
                    />
                </div>
            </BaseCollapse.Header>
            <BaseCollapse.Body className="!p-0">
                <div className="flex flex-col">
                    <div className="max-h-[450px] overflow-y-auto p-[16px] space-y-4">
                        {mappings.map((mapping, index) => (
                            <div key={mapping.id} className="flex items-center gap-2">
                                <div className="flex-1">
                                    <ShopifyLocationDropdown
                                        control={control}
                                        index={index}
                                        availableShopifyLocations={getAvailableShopifyOptions(
                                            index,
                                        )}
                                        isLoading={isShopifyOptionsLoading}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <ArrowRight className="w-5 h-5 text-gray-400" />
                                </div>
                                <div className="flex-1">
                                    <SuppliLocationDropdown
                                        control={control}
                                        index={index}
                                        availableSuppliLocations={getAvailableSuppliOptions(index)}
                                        isLoading={isSuppliOptionsLoading}
                                    />
                                </div>
                                {index === mappings.length - 1 ? (
                                    <BaseMaterialIconButton
                                        icon={<Plus size={14} />}
                                        size="small"
                                        style="filled"
                                        onClick={() =>
                                            append({
                                                shopify_location: null,
                                                suppli_location: null,
                                            })
                                        }
                                    />
                                ) : (
                                    <BaseMaterialIconButton
                                        icon={<TrashSVG width={14} height={14} />}
                                        size="small"
                                        style="filled"
                                        onClick={() => remove(index)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </BaseCollapse.Body>
        </BaseCollapse>
    );
}
